import { zodResolver } from '@hookform/resolvers/zod';
import { api } from '@packages/backend/convex/_generated/api';
import {
  getUserErrorMessage,
  type WebsiteConfig,
  type WebsiteConfigCvx,
  websiteConfigStrictSchema,
} from '@packages/shared';
import { createFileRoute } from '@tanstack/react-router';
import { useConvex } from 'convex/react';
import { useQuery } from 'convex-helpers/react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { ActionButton } from '@/components/ActionButton';
import { ErrorAlert } from '@/components/ErrorAlert';
import { FormWrapper } from '@/components/FormWrapper';
import { LoadingIcon } from '@/components/LoadingIcon';
import { NullableInput } from '@/components/NullableInput';
import { UploadButton } from '@/components/UploadButton';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { handleSubmitErrors } from '@/lib/handleSubmitErrors';
import { HtmlEditor } from '@/routes/restaurant/settings/website-config/HtmlEditor';

export const Route = createFileRoute('/r/$restaurantId/s/website-config')({
  component: WebsiteConfigPage,
});

function WebsiteConfigPage() {
  const { restaurantId } = Route.useParams();
  const {
    data: websiteConfig,
    error,
    isError,
    isPending,
  } = useQuery(api.websiteConfigs.getByRestaurantId, { restaurantId });

  if (isPending) return <LoadingIcon />;

  if (isError) {
    return <ErrorAlert message={error.message} title="Error loading website config" />;
  }

  return <WebsiteConfigForm websiteConfig={websiteConfig} />;
}

type WebsiteConfigFormProps = {
  websiteConfig: WebsiteConfigCvx | null;
};

const WebsiteConfigForm = ({ websiteConfig }: WebsiteConfigFormProps) => {
  const { restaurantId } = Route.useParams();
  const convex = useConvex();

  const form = useForm({
    defaultValues: websiteConfig ?? {},
    resolver: zodResolver(websiteConfigStrictSchema),
  });

  // Subscribe to changes
  useEffect(() => {
    if (!websiteConfig) return;
    // Ensure un-saved user changes are kept on Convex sync
    form.reset(websiteConfig, { keepDirty: true, keepDirtyValues: true });
  }, [form, websiteConfig]);

  const onValidSubmit = async (formValues: WebsiteConfig) => {
    try {
      await convex.mutation(api.websiteConfigs.save, {
        restaurantId,
        websiteConfig: formValues,
      });
      // The saved values are the new baseline, so nothing is dirty any more
      form.reset(form.getValues());
      toast.success('Saved successfully');
    } catch (error) {
      const description = getUserErrorMessage(error);
      toast.error('Error saving!', { description });
    }
  };

  const handleDeleteImage = async (type: 'banner' | 'aboutUs') => {
    try {
      await convex.mutation(api.websiteConfigs.deleteImage, { restaurantId, type });
    } catch (error) {
      toast.error("Couldn't delete the image", { description: getUserErrorMessage(error) });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Website Config</CardTitle>
        <CardDescription>Custom website with online ordering</CardDescription>
      </CardHeader>
      <CardContent>
        <FormWrapper {...form}>
          <form
            id="form"
            onSubmit={form.handleSubmit(onValidSubmit, handleSubmitErrors)}
            className="grid gap-6"
          >
            {/* Domain name */}
            <FormField
              control={form.control}
              name="domainName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Custom domain</FormLabel>
                  <FormControl>
                    <NullableInput {...field} placeholder={'E.g. myrestaurant.com'} />
                  </FormControl>
                  <FormDescription>
                    Optional. The venue's site forwards to it once it's set.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Facebook URL */}
            <FormField
              control={form.control}
              name="facebookUrl"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Facebook URL</FormLabel>
                  <FormControl>
                    <NullableInput {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Instagram URL */}
            <FormField
              control={form.control}
              name="instagramUrl"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Instagram URL</FormLabel>
                  <FormControl>
                    <NullableInput {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* About us */}
            <FormField
              control={form.control}
              name="aboutUsContent"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>About us</FormLabel>
                  <FormControl>
                    <HtmlEditor onChange={field.onChange} value={field.value ?? null} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Images (can only be added once initial config is set up) */}
            {!!websiteConfig?._id && (
              <>
                <div className={'flex gap-4 justify-end'}>
                  {!websiteConfig.bannerImageId ? (
                    <UploadButton
                      buttonText="Upload banner image"
                      fileTypes={['.jpg']}
                      imageHeight={270}
                      imageWidth={970}
                      onUploadComplete={async (storageId) => {
                        await convex.mutation(api.websiteConfigs.saveImage, {
                          restaurantId,
                          storageId,
                          type: 'banner',
                        });
                        toast.success('Banner uploaded successfully');
                      }}
                    />
                  ) : (
                    <>
                      <div className="flex flex-col w-full">
                        {websiteConfig.bannerImageId && (
                          <div className={'text-muted-foreground text-sm'}>
                            {websiteConfig.bannerImageId}
                          </div>
                        )}
                        {websiteConfig.bannerImageUrl && (
                          <a
                            className={
                              'text-blue-600 hover:text-blue-800 underline hover:no-underline text-sm'
                            }
                            href={websiteConfig.bannerImageUrl}
                            rel="noreferrer"
                            target="_blank"
                          >
                            {websiteConfig.bannerImageUrl}
                          </a>
                        )}
                      </div>
                      <ActionButton
                        onClick={async (e) => {
                          e.preventDefault();
                          await handleDeleteImage('banner');
                        }}
                        variant={'destructive'}
                      >
                        Delete Banner
                      </ActionButton>
                    </>
                  )}
                </div>

                {/* About us image */}
                <div className={'flex gap-4 justify-end'}>
                  {!websiteConfig.aboutUsImageId ? (
                    <UploadButton
                      buttonText="Upload about us image"
                      fileTypes={['.jpg']}
                      // imageHeight={270}
                      // imageWidth={970}
                      onUploadComplete={async (storageId) => {
                        await convex.mutation(api.websiteConfigs.saveImage, {
                          restaurantId,
                          storageId,
                          type: 'aboutUs',
                        });
                        toast.success('About us uploaded successfully');
                      }}
                    />
                  ) : (
                    <>
                      <div className="flex flex-col w-full">
                        {websiteConfig.aboutUsImageId && (
                          <div className={'text-muted-foreground text-sm'}>
                            {websiteConfig.aboutUsImageId}
                          </div>
                        )}
                        {websiteConfig.aboutUsImageUrl && (
                          <a
                            className={
                              'text-blue-600 hover:text-blue-800 underline hover:no-underline text-sm'
                            }
                            href={websiteConfig.aboutUsImageUrl}
                            rel="noreferrer"
                            target="_blank"
                          >
                            {websiteConfig.aboutUsImageUrl}
                          </a>
                        )}
                      </div>
                      <ActionButton
                        onClick={async (e) => {
                          e.preventDefault();
                          await handleDeleteImage('aboutUs');
                        }}
                        variant={'destructive'}
                      >
                        Delete About Us
                      </ActionButton>
                    </>
                  )}
                </div>
              </>
            )}
          </form>
        </FormWrapper>
      </CardContent>
      <CardFooter className="border-t">
        <ActionButton
          disabled={form.formState.isSubmitting || !form.formState.isDirty}
          loading={form.formState.isSubmitting}
          type="submit"
          form="form"
        >
          Save
        </ActionButton>
      </CardFooter>
    </Card>
  );
};
